/**
 * Effective state of an OrgUnit membership.
 *
 * `isActive` alone does not decide whether a membership counts: every server-side consumer
 * (IPrincipalResolver for plan entry, IOrgUnitMemberService.GetUserMembershipsAsync for the
 * consolidation scope) filters on `isActive && validFrom <= now <= validTo`. A row can therefore be
 * flagged active and still grant nothing, which is exactly what a green "Active" chip hid: an expired
 * membership looked identical to a live one, so "why does this user not see their org unit?" had no
 * answer on screen.
 *
 * Mirrors `OrgUnitMemberService.ApplyActiveFilter` — keep the two in step.
 */

export type MembershipState = 'ACTIVE' | 'EXPIRED' | 'NOT_YET_VALID' | 'INACTIVE' | 'INVALID_RANGE';

export interface MembershipValidity {
  isActive: boolean;
  validFrom?: string | null;
  validTo?: string | null;
}

/**
 * What this membership grants *right now*. `now` is injectable so the result is testable without
 * freezing the clock.
 *
 * `INVALID_RANGE` (validTo before validFrom) can never be satisfied at any instant. It is reported
 * separately rather than as EXPIRED because the two need different fixes — one is a lapsed
 * membership, the other is data that should never have been written.
 */
export function membershipState(
  member: MembershipValidity,
  now: Date = new Date(),
): MembershipState {
  if (!member.isActive) return 'INACTIVE';

  const from = member.validFrom ? new Date(member.validFrom) : null;
  const to = member.validTo ? new Date(member.validTo) : null;

  if (from && to && to < from) return 'INVALID_RANGE';
  if (from && from > now) return 'NOT_YET_VALID';
  if (to && to < now) return 'EXPIRED';
  return 'ACTIVE';
}

/** Does this membership grant access right now? The client-side mirror of the server's filter. */
export function membershipCountsNow(member: MembershipValidity, now: Date = new Date()): boolean {
  return membershipState(member, now) === 'ACTIVE';
}

/** Chip colour per state — only ACTIVE is success, so "green" keeps meaning "this grants access". */
export function membershipStateColor(
  state: MembershipState,
): 'success' | 'warning' | 'info' | 'error' | 'default' {
  switch (state) {
    case 'ACTIVE': return 'success';
    case 'EXPIRED': return 'warning';
    case 'NOT_YET_VALID': return 'info';
    case 'INVALID_RANGE': return 'error';
    case 'INACTIVE': return 'default';
  }
}

/** Date-only rendering for the validity columns; an empty bound reads as "no limit", not as a blank. */
export function formatValidityBound(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toISOString().slice(0, 10);
}

/**
 * A date from a `<input type="date">` widened to the instant the user means.
 *
 * `validTo` is compared with `>= now`, so storing a bare date would expire the membership at 00:00 on
 * the day the user named as its last valid day — silently one day short. End-of-day is the reading
 * that matches "valid to and including".
 */
export function validityBoundToIso(
  dateOnly: string,
  bound: 'from' | 'to',
): string | undefined {
  if (!dateOnly) return undefined;
  return bound === 'from' ? `${dateOnly}T00:00:00` : `${dateOnly}T23:59:59`;
}

/** The inverse, for seeding a date input from a stored instant. */
export function isoToDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}
